import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'

const READING_GOAL_QUERY_KEY = 'reading-goal'

/** The user's book target for a year, or null when not set */
export function useReadingGoal(year: number) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [READING_GOAL_QUERY_KEY, user?.id, year],
    queryFn: async (): Promise<number | null> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('reading_goals')
        .select('target')
        .eq('user_id', user.id)
        .eq('year', year)
        .maybeSingle()

      if (error) {
        captureError(error, { context: 'useReadingGoal', userId: user.id, year })
        throw error
      }
      return data?.target ?? null
    },
    enabled: !!user?.id,
  })
}

/** Creates or updates the goal for a year; target null removes it */
export function useSetReadingGoal() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ year, target }: { year: number; target: number | null }) => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } =
        target === null
          ? await supabase.from('reading_goals').delete().eq('user_id', user.id).eq('year', year)
          : await supabase
              .from('reading_goals')
              .upsert({ user_id: user.id, year, target }, { onConflict: 'user_id,year' })

      if (error) {
        captureError(error, { context: 'useSetReadingGoal', userId: user.id, year })
        throw error
      }
    },
    onSuccess: (_data, { year }) => {
      queryClient.invalidateQueries({ queryKey: [READING_GOAL_QUERY_KEY, user?.id, year] })
    },
  })
}
